// src/HymnList.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { collection, getDocs, orderBy, query, updateDoc, doc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// 🔹 Utils
import { slugify, normalize, getFirstLineFromLyrics } from "./utils/helpers";
import highlightMatch from "./utils/highlightMatch";
import { ensureStructuredFromDoc } from "./utils/lyrics";

// 🔹 Modules
import HymnSearchBar from "./modules/HymnSearchBar";
import SortMenu from "./modules/SortMenu";
import ListLoader from "./modules/ListLoader";
import HymnAutoMigrate from "./modules/HymnAutoMigrate";
import useInstantRefresh from "./modules/useInstantRefresh";

export default function HymnList() {
  const [hymns, setHymns] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const navigate = useNavigate();

  // 🔁 Instant Refresh Hook (for new additions)
  useInstantRefresh("hymns", setHymns);

  useEffect(() => {
    loadHymns();
  }, []);

  const loadHymns = async () => {
    try {
      setLoading(true);
      const hymnsRef = collection(db, "hymns");
      const qy = query(hymnsRef, orderBy("createdAt", "desc"));
      const snap = await getDocs(qy);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHymns(data);
      setFiltered(data);
    } catch (e) {
      console.error("Failed to load hymns:", e);
      toast.error("Failed to load hymns");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 Search filter
  useEffect(() => {
    const term = normalize(searchTerm);
    if (!term) return setFiltered(hymns);

    const filteredList = hymns.filter(
      (h) =>
        normalize(h.title).includes(term) ||
        normalize(h.author_name).includes(term) ||
        normalize(h.key_label).includes(term)
    );
    setFiltered(filteredList);
  }, [searchTerm, hymns]);

  // 🔄 Auto-migrate old hymns (button only)
  const handleAutoMigrate = async () => {
    try {
      setMigrating(true);
      const hymnsRef = collection(db, "hymns");
      const qy = query(hymnsRef);
      const snap = await getDocs(qy);

      let upgradedCount = 0;
      for (const docSnap of snap.docs) {
        const h = docSnap.data();
        if (!h.lyrics || h.lyrics_structured) continue;
        const structured = ensureStructuredFromDoc(h.lyrics);
        await updateDoc(doc(db, "hymns", docSnap.id), {
          lyrics_structured: structured,
        });
        upgradedCount++;
      }
      toast.success(`Upgraded ${upgradedCount} hymns successfully.`);
      await loadHymns();
    } catch (e) {
      console.error("Auto-migrate failed:", e);
      toast.error("Migration failed. Check console for details.");
    } finally {
      setMigrating(false);
    }
  };

  // 🧮 Sort handler
  const handleSortChange = (mode) => {
    setSortMode(mode);
    let sorted = [...filtered];
    if (mode === "az") sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (mode === "number") sorted.sort((a, b) => (a.master_number || 0) - (b.master_number || 0));
    else sorted.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setFiltered(sorted);
  };

  // 🧱 UI
  if (loading) return <ListLoader text="Loading hymns…" />;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          All Hymns ({filtered.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Bulk upgrade old hymns">
          </Tooltip>
          <Tooltip title="Add new hymn">
            <IconButton color="primary" onClick={() => navigate("/add")}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Search + Sort */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <HymnSearchBar value={searchTerm} onChange={setSearchTerm} />
        <SortMenu value={sortMode} onChange={handleSortChange} />
      </Stack>

      {/* Hymn list */}
      {filtered.length === 0 ? (
        <Typography>No hymns found.</Typography>
      ) : (
        filtered.map((h) => (
          <Paper
            key={h.id}
            sx={{
              mb: 2,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#fff",
              border: "1px solid #eee",
              transition: "0.2s",
              "&:hover": { borderColor: "#ccc", bgcolor: "#fafafa" },
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {h.master_number ? `#${h.master_number} — ` : ""}{" "}
                {highlightMatch(h.title, searchTerm)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {h.author_name ? `By: ${h.author_name}` : ""}
                {h.key_label ? `  •  Key: ${h.key_label}` : ""}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {getFirstLineFromLyrics(h.lyrics || "")}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="View hymn">
                <IconButton color="primary" onClick={() => navigate(`/view/${h.id}`)}>
                  <PreviewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit hymn">
                <IconButton color="secondary" onClick={() => navigate(`/edit/${h.id}`)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        ))
      )}
    </Box>
  );
}
